"use client";

import { LifeBuoy, Check, X } from "lucide-react";
import Logo from "@/icons/logo";
import { useWidgetStore } from "../stores/widget-store";
import { saveWidgetConfig } from "../services/widget";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";

export function Header() {
  const [isSaving, setIsSaving] = useState(false);
  const status = useWidgetStore((state) => state.config.status);
  const config = useWidgetStore((state) => state.config);
  const setStatus = useWidgetStore((state) => state.setStatus);
  const { toast } = useToast();

  // Widget is considered enabled if both desktop and mobile are true
  const isEnabled = status.desktop || status.mobile;

  const handleToggle = async () => {
    const newStatus = {
      desktop: !isEnabled,
      mobile: !isEnabled
    };
    
    setStatus(newStatus);
    setIsSaving(true);

    try {
      await saveWidgetConfig({
        ...config,
        status: newStatus
      });

      toast({
        title: `Widget ${!isEnabled ? "enabled" : "disabled"} successfully`,
        variant: "success",
      });
    } catch {
      toast({
        title: "Failed to save changes",
        variant: "destructive",
      });
      // Revert the status if save failed
      setStatus(status);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="cy-header cy:border-b cy:border-gray-200">
      <div className="cy:max-w-7xl cy:mx-auto cy:px-4 cy:sm:px-6 cy:lg:px-8 ">
        <div className="cy:flex cy:justify-between cy:items-center cy:py-4">
          <div className="cy:flex cy:items-center cy:space-x-3">
            <div className="cy-flex">
              <Logo className="cy:w-[157px] cy:h-[37px] cy:text-white" />
            </div>
          </div>
          <div className="cy:flex cy:items-center cy:space-x-12">
            {/* Widget Status Toggle */}
            <div className="cy:flex cy:items-center cy:bg-[#00000045] cy:rounded-lg cy:px-4 cy:py-2">
              <div className="cy:flex cy:flex-col cy:mr-3">
                <span className="cy:text-sm cy:text-white">Widget status</span>
                <div className="cy:flex cy:items-center cy:space-x-2">
                  <div className={`cy:w-5 cy:h-5 cy:rounded-full cy:flex cy:items-center cy:justify-center ${
                    isEnabled ? "cy:bg-[#2DAD70]" : "cy:bg-[#DD425A]"
                  }`}>
                    {isEnabled ? (
                      <Check className="cy:w-3.5 cy:h-3.5 cy:text-white" />
                    ) : (
                      <X className="cy:w-3.5 cy:h-3.5 cy:text-white" />
                    )}
                  </div>
                  <span className="cy:text-base cy:font-medium cy:text-white">
                    {isEnabled ? "Enabled" : "Disabled"}
                  </span>
                </div>
              </div>
              <button
                onClick={handleToggle}
                disabled={isSaving}
                className={`cy:w-12 cy:h-6 cy:rounded-full cy:relative cy:transition-colors ${
                  isEnabled ? "cy:bg-[#1578F7]" : "cy:bg-[#8F92A1]"
                } ${isSaving ? "cy:opacity-50 cy:cursor-not-allowed" : ""}`}
                aria-checked={isEnabled}
                role="switch"
              >
                <span
                  className={`cy:block cy:w-5 cy:h-5 cy:bg-white cy:rounded-full cy:transition-transform cy:absolute cy:top-0.5 cy:left-0.5
                    ${isEnabled ? "cy:translate-x-6" : "cy:translate-x-0"}`}
                />
              </button>
            </div>
            <a
              href="https://www.cookieyes.com/accessibility-widget-support/"
              target="_blank"
              className="cy:inline-flex cy:items-center cy:justify-center cy:whitespace-nowrap cy:text-sm cy:transition-all cy:disabled:pointer-events-none cy:disabled:opacity-50 cy:[&_svg]:pointer-events-none  cy:shrink-0 cy:[&_svg]:shrink-0 cy:outline-none cy:focus-visible:border-ring cy:focus-visible:ring-ring/50 cy:focus-visible:ring-[3px] cy:aria-invalid:ring-destructive/20 cy:dark:aria-invalid:ring-destructive/40 cy:aria-invalid:border-destructive cy:bg-transparent cy:h-8 cy:rounded-md cy:gap-1.5 cy:px-3 cy:border-1 cy:border-white cy:text-white! cy:hover:bg-white cy:hover:text-black!"
            >
              <LifeBuoy className="cy:h-4 cy:w-4" /> Help
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
